import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { GoalsSkeleton } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/goalsService'
import GoalDialog from './components/GoalDialog'
import GoalCheckInDialog from './components/GoalCheckInDialog'

const ProgressBar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  position: relative;
  overflow: hidden;
  margin-top: 12px;
`
const ProgressFill = styled.div`
  position: absolute;
  inset: 0;
  width: ${(p) => p.$pct}%;
  background: ${(p) => p.theme?.color?.accent || '#6B9080'};
  border-radius: 999px;
  transition: width 200ms ease-out;
`

export default function GoalsPage() {
  const { currentProfile } = usePatient()
  const [goals, setGoals] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [creating, setCreating] = useState(false)
  const [checkInTarget, setCheckInTarget] = useState(null)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const data = await svc.list(currentProfile, 'ACTIVE')
      setGoals(data)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setGoals(null)
    refresh()
  }, [refresh])

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/care">Back to Care</BackLink>
        <Row $between $wrap>
          <div>
            <Pill>Care</Pill>
            <PageHeading style={{ marginTop: 12 }}>Goals</PageHeading>
            <PageSubheading>
              Set personal goals or track ones your practitioner shared.
            </PageSubheading>
          </div>
          <GhostButton onClick={() => setCreating(true)}>+ New goal</GhostButton>
        </Row>

        {loading && !goals ? (
          <GoalsSkeleton />
        ) : !goals?.length ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>No active goals.</p>
            <Muted>Set one to give your week some direction.</Muted>
          </div>
        ) : (
          <Stack $gap="12px">
            {goals.map((g) => (
              <Card key={g.id}>
                <Row $between $wrap $gap="10px">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: '#1F2937',
                        marginBottom: 2,
                      }}
                    >
                      {g.title}
                    </div>
                    {g.description && <Muted>{g.description}</Muted>}
                    <Muted style={{ marginTop: 4, fontSize: 12 }}>
                      Set by {g.setBy === 'SELF' ? 'you' : g.setBy}
                    </Muted>
                  </div>
                  <Row $gap="6px">
                    <GhostButton onClick={() => setCheckInTarget(g)}>Check in</GhostButton>
                    <button
                      onClick={() => setEditing(g)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#6B7280',
                        fontSize: 13,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Edit
                    </button>
                  </Row>
                </Row>
                <ProgressBar>
                  <ProgressFill $pct={Math.max(0, Math.min(100, g.progressPct || 0))} />
                </ProgressBar>
                <Muted style={{ marginTop: 6, fontSize: 12 }}>
                  {g.progressPct ?? 0}%{g.targetValue != null ? ` · ${g.currentValue ?? 0} of ${g.targetValue}` : ''}
                  {g.unit ? ` ${g.unit}` : ''}
                </Muted>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      <GoalDialog
        open={creating}
        existing={null}
        onClose={() => setCreating(false)}
        onSubmit={async (body) => {
          await svc.create(currentProfile, body)
          await refresh()
        }}
      />
      <GoalDialog
        open={!!editing}
        existing={editing}
        onClose={() => setEditing(null)}
        onSubmit={async (body) => {
          await svc.update(currentProfile, editing.id, body)
          await refresh()
        }}
      />
      <GoalCheckInDialog
        open={!!checkInTarget}
        goal={checkInTarget}
        onClose={() => setCheckInTarget(null)}
        onSubmit={async (body) => {
          await svc.checkIn(currentProfile, checkInTarget.id, body)
          await refresh()
        }}
      />
    </ContentMaxWidth>
  )
}
